import { Button } from "@/components/ui/button";

interface TablePagerProps {
  pageNum: number;
  totalPages: number;
  pageSize?: number;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  /** Narrow panels (agent stream) — smaller controls. */
  compact?: boolean;
}

export function TablePager({
  pageNum,
  totalPages,
  isLoading = false,
  onPageChange,
  compact = false,
}: TablePagerProps) {
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(n => n === 1 || n === totalPages || Math.abs(n - pageNum) <= 1)
    .reduce<(number | "...")[]>((acc, n, idx, arr) => {
      if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push("...");
      acc.push(n);
      return acc;
    }, []);

  return (
    <div
      className={
        compact
          ? "flex items-center justify-center gap-1 border-t border-border bg-card px-2 py-1.5"
          : "flex items-center justify-end gap-1.5 border-t border-border bg-card px-4 py-2.5"
      }
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={
          compact
            ? "h-7 px-2 text-xs font-medium"
            : "h-8 px-2.5 text-sm font-medium"
        }
        disabled={pageNum <= 1 || isLoading}
        onClick={() => onPageChange(Math.max(1, pageNum - 1))}
      >
        ← Prev
      </Button>
      {pages.map((item, idx) =>
        item === "..." ? (
          <span
            key={`el-${idx}`}
            className={
              compact
                ? "px-0.5 text-xs text-muted-foreground"
                : "px-1 text-sm text-muted-foreground"
            }
          >
            …
          </span>
        ) : (
          <Button
            key={item}
            type="button"
            variant={pageNum === item ? "default" : "outline"}
            size="sm"
            className={
              compact
                ? "h-7 w-7 p-0 text-xs font-semibold"
                : "h-8 w-8 p-0 text-sm font-semibold"
            }
            disabled={isLoading}
            onClick={() => onPageChange(item as number)}
          >
            {item}
          </Button>
        )
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={
          compact
            ? "h-7 px-2 text-xs font-medium"
            : "h-8 px-2.5 text-sm font-medium"
        }
        disabled={pageNum >= totalPages || isLoading}
        onClick={() => onPageChange(Math.min(totalPages, pageNum + 1))}
      >
        Next →
      </Button>
    </div>
  );
}
